/**
 * The staff directory: the people a picker offers for an owner, an assignee
 * or a partner field.
 *
 * It is two Auger lists, not the `users` table. `heavybit-employees` holds the
 * person record of every active member of the Heavybit Stytch organization,
 * and `heavybit-partners` the ones that hold the `heavybit_partner` role. A
 * sync job in the auger-jobs stack keeps both in step with Stytch (workers/staffLists),
 * so the lists are copies: a hand edit is undone at its next run.
 *
 * A directory entry is keyed by the PERSON record id. `record_id` repeats it
 * for the callers that read a user's record id.
 */
import type { User } from "./registries/users.js"

/** The Heavybit organization record: the org of every staff membership. */
export const HEAVYBIT_ORG_RECORD_ID = 23915

/** The list of the Heavybit staff, by slug (one list has it; slugs are per type). */
export const STAFF_LIST_SLUG = "heavybit-employees"
/** The list of the Heavybit partners, by slug. */
export const PARTNERS_LIST_SLUG = "heavybit-partners"

export interface DirectoryPersonRow {
  id: number
  name?: string | null
  slug?: string | null
  /** The person's email contacts, primary first. */
  contacts?: ReadonlyArray<{ value?: string | null }> | null
}

export interface DirectoryRows<P extends DirectoryPersonRow = DirectoryPersonRow> {
  /** The listings of the staff list, each with its person record. */
  staff: ReadonlyArray<{ record?: P | null }>
  /** The listings of the partners list. */
  partners: ReadonlyArray<{ record_id: number }>
}

/**
 * The directory entries, sorted by name. A listing whose record did not load
 * (archived, or not readable) is left out. `extra` adds host fields to each
 * entry from its person row (the frontend keeps the whole record for avatars);
 * the entry type carries them.
 */
export function buildDirectory<P extends DirectoryPersonRow>(rows: DirectoryRows<P>): User[]
export function buildDirectory<P extends DirectoryPersonRow, E extends object>(
  rows: DirectoryRows<P>,
  extra: (person: P) => E
): Array<User & E>
export function buildDirectory<P extends DirectoryPersonRow, E extends object>(
  rows: DirectoryRows<P>,
  extra?: (person: P) => E
): Array<User | (User & E)> {
  const partnerIds = new Set(rows.partners.map((p) => p.record_id))
  const seen = new Set<number>()
  const entries: Array<User | (User & E)> = []
  for (const listing of rows.staff) {
    const person = listing.record
    if (!person || seen.has(person.id)) continue
    seen.add(person.id)
    const entry: User = {
      id: person.id,
      record_id: person.id,
      name: person.name ?? null,
      slug: person.slug ?? null,
      email: person.contacts?.[0]?.value ?? null,
      partner: partnerIds.has(person.id),
      active: true,
    }
    entries.push(extra ? { ...extra(person), ...entry } : entry)
  }
  return entries.sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? "")))
}
